import { useMemo, useRef } from 'react';
import { shouldUseDsoStructure } from '~/growthbook';
import { useAppSelector } from '~/hooks/useAppSelector';
import type { TViewedOfficeScope } from '~/types/ViewedOfficeScope';

/**
 * The offices a Payment History request names for one location or for the whole
 * organization (GOTU-19472), shared by the table and its Download Report so the report
 * covers the rows on screen. `undefined` for the selected office: each caller keeps its
 * own rule for that.
 */
export const useScopeReceiptOfficeIds = (scope: TViewedOfficeScope): string[] | undefined => {
  const isDSOStructureEnabled = useRef(shouldUseDsoStructure()).current;
  const offices = useAppSelector((state) => state.user.offices?.map((office) => office.id));
  const childOffices = useAppSelector((state) =>
    state.user.childOffices?.map((office) => office.id),
  );

  return useMemo(() => {
    if (scope.kind === 'location') {
      return [scope.officeId];
    }

    // Every office the user can access, as the parent's view has always built it.
    if (scope.kind === 'organization') {
      return (isDSOStructureEnabled ? offices : childOffices) ?? [];
    }

    return undefined;
  }, [childOffices, isDSOStructureEnabled, offices, scope]);
};
